import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Checkbox } from "../ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { User, UserCog, Users, ChevronRight, Save } from "lucide-react";
import { cn } from "../../lib/utils";

const GIORNI_SHORT = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];

export const PreferenzeDialogs = ({
  dialogListOpen, onDialogListChange,
  dialogEditOpen, onDialogEditChange,
  operatori, preferenze, fasce,
  operatorePreferenze, prefForm, onPrefFormChange,
  onOpenPreferenze, onSavePreferenze,
  onToggleTurnoEscluso, onToggleGiornoRiposo
}) => (
  <>
    <Dialog open={dialogListOpen} onOpenChange={onDialogListChange}>
      <DialogContent className="max-w-md max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><UserCog className="h-5 w-5 text-primary" />Preferenze Turni Operatori</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground mb-4">Configura per ogni operatore: giorni di riposo, turni esclusi e altre preferenze per la generazione automatica dei turni.</p>
        <div className="space-y-2">
          {operatori.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground"><Users className="h-12 w-12 mx-auto mb-3 opacity-50" /><p>Nessun operatore disponibile</p></div>
          ) : (
            operatori.map(op => {
              const pref = preferenze[op.id];
              return (
                <div key={op.id} className="flex items-center justify-between p-3 rounded-lg border bg-card hover:bg-accent/50 cursor-pointer transition-colors" onClick={() => onOpenPreferenze(op)}>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center"><User className="h-5 w-5 text-primary" /></div>
                    <div>
                      <p className="font-medium">{op.nome}</p>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        {pref ? (<><span>{pref.giorni_riposo || 1} riposi/sett</span>{pref.turni_esclusi?.length > 0 && <span className="text-amber-500">• {pref.turni_esclusi.length} turni esclusi</span>}</>) : (<span className="text-muted-foreground/50">Non configurato</span>)}
                      </div>
                    </div>
                  </div>
                  <ChevronRight className="h-5 w-5 text-muted-foreground" />
                </div>
              );
            })
          )}
        </div>
        <DialogFooter><Button variant="outline" onClick={() => onDialogListChange(false)}>Chiudi</Button></DialogFooter>
      </DialogContent>
    </Dialog>

    <Dialog open={dialogEditOpen} onOpenChange={onDialogEditChange}>
      <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><User className="h-5 w-5 text-primary" />Preferenze di {operatorePreferenze?.nome}</DialogTitle>
        </DialogHeader>
        <div className="space-y-6">
          <div className="space-y-3">
            <Label className="text-sm font-semibold">Giorni di Riposo Settimanali</Label>
            <p className="text-xs text-muted-foreground">Quanti giorni di riposo deve avere questo operatore ogni settimana?</p>
            <Select value={String(prefForm.giorni_riposo || 1)} onValueChange={(v) => onPrefFormChange({ ...prefForm, giorni_riposo: parseInt(v) })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{[1, 2, 3, 4, 5].map(n => (<SelectItem key={n} value={String(n)}>{n} giorn{n === 1 ? 'o' : 'i'}</SelectItem>))}</SelectContent>
            </Select>
          </div>
          <div className="space-y-3">
            <Label className="text-sm font-semibold">Giorni Possibili per il Riposo</Label>
            <p className="text-xs text-muted-foreground">In quali giorni della settimana può riposare?</p>
            <div className="grid grid-cols-7 gap-1">
              {GIORNI_SHORT.map((giorno, idx) => {
                const isSelected = (prefForm.giorni_riposo_disponibili || [5, 6]).includes(idx);
                return (
                  <button key={idx} type="button" onClick={() => onToggleGiornoRiposo(idx)} className={cn("py-2 px-1 rounded-lg text-xs font-medium transition-all", isSelected ? "bg-primary text-primary-foreground" : "bg-muted hover:bg-muted/80")}>{giorno}</button>
                );
              })}
            </div>
          </div>
          <div className="space-y-3">
            <Label className="text-sm font-semibold">Turni Esclusi</Label>
            <p className="text-xs text-muted-foreground">Quali turni NON può fare questo operatore?</p>
            {fasce.length === 0 ? (
              <p className="text-sm text-muted-foreground italic">Nessuna fascia turno configurata</p>
            ) : (
              <div className="space-y-2">
                {fasce.map(fascia => {
                  const isExcluded = (prefForm.turni_esclusi || []).includes(fascia.id);
                  return (
                    <div key={fascia.id} className={cn("flex items-center justify-between p-3 rounded-lg border transition-colors cursor-pointer", isExcluded ? "bg-red-50 border-red-200 dark:bg-red-950/20" : "bg-card hover:bg-accent/50")} onClick={() => onToggleTurnoEscluso(fascia.id)}>
                      <div className="flex items-center gap-3">
                        <div className="w-3 h-8 rounded-full" style={{ backgroundColor: fascia.colore || "#3B82F6" }} />
                        <div><p className="font-medium text-sm">{fascia.nome}</p><p className="text-xs text-muted-foreground">{fascia.ora_inizio} - {fascia.ora_fine}</p></div>
                      </div>
                      <Checkbox checked={isExcluded} />
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
        <DialogFooter className="flex-col sm:flex-row gap-2 mt-4">
          <Button variant="outline" onClick={() => onDialogEditChange(false)} className="flex-1">Annulla</Button>
          <Button onClick={onSavePreferenze} className="flex-1 bg-primary"><Save className="h-4 w-4 mr-2" />Salva Preferenze</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  </>
);
